import { useTranslation } from 'react-i18next';

import { Async } from '@/shared/ui/Async';
import { Badge } from '@/shared/ui/Badge';

import { useAuditLog } from '../hooks/use-admin';

function actionColor(action: string): string {
  if (action === 'verify' || action.endsWith('_resolved') || action === 'ticket_resolved')
    return 'var(--color-success)';
  if (action.startsWith('unverif') || action === 'ticket_closed')
    return 'var(--color-on-surface-muted)';
  if (action.startsWith('phone_reveal')) return 'var(--color-accent-text)';
  return 'var(--color-primary)';
}

/** Audit log — Phase 4 audit_log table read by admins. Renders every recorded
 * admin action (verify/certifier grants, ticket transitions, phone-reveal
 * events, retention writes) newest-first. */
export function AdminAuditRoute(): JSX.Element {
  const { t } = useTranslation();
  const query = useAuditLog(200);
  return (
    <main className="page stack">
      <h1>{t('admin.auditTitle')}</h1>
      <p className="muted">{t('admin.auditHint')}</p>
      <Async query={query} isEmpty={(l) => l.length === 0} emptyMessage={t('admin.auditEmpty')}>
        {(list) => (
          <div className="stack">
            {list.map((entry) => (
              <div key={entry.id} className="card stack">
                <div className="row row--between">
                  <div className="wrap">
                    <Badge label={entry.action} color={actionColor(entry.action)} />
                    <span className="muted">{entry.targetType}</span>
                  </div>
                  <span className="muted">{new Date(entry.createdAt).toLocaleString()}</span>
                </div>
                <span className="muted">
                  {t('admin.auditActor', { name: entry.actorName ?? t('admin.auditActorSystem') })}
                </span>
                {entry.targetId ? (
                  <span className="muted" style={{ fontFamily: 'monospace', fontSize: '0.85em' }}>
                    {entry.targetId}
                  </span>
                ) : null}
                {Object.keys(entry.detail).length > 0 ? (
                  <pre
                    className="muted"
                    style={{
                      whiteSpace: 'pre-wrap',
                      fontSize: '0.85em',
                      margin: 0,
                    }}
                  >
                    {JSON.stringify(entry.detail, null, 2)}
                  </pre>
                ) : null}
              </div>
            ))}
          </div>
        )}
      </Async>
    </main>
  );
}
